import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useBrandManager } from './useBrandManager';
import { useEffect } from 'react';
import { usePageVisibility, useAdaptiveRefetchInterval } from './usePageVisibility';

/**
 * Hook to get unread message counts per thread for the current user
 * Returns a map of threadId -> unreadCount
 */
export const useSupportThreadUnreadCounts = () => {
  const { user } = useAuth();
  const { currentBrandId } = useBrandManager();
  const { isVisible } = usePageVisibility();
  
  // Only refetch when tab is visible
  const refetchInterval = useAdaptiveRefetchInterval(60000, false); // 60s when visible, disabled when hidden

  const { data: unreadCounts = {}, refetch } = useQuery({
    queryKey: ['support-thread-unread-counts', user?.id, currentBrandId],
    queryFn: async () => {
      if (!user?.id || !currentBrandId) return {};

      // Get all threads for the current brand
      const { data: threads, error: threadsError } = await supabase
        .from('support_threads')
        .select('id')
        .eq('brand_id', currentBrandId);

      if (threadsError || !threads || threads.length === 0) return {};

      const threadIds = threads.map(t => t.id);

      // Get unread message counts grouped by thread_id
      const { data: unreadMessages, error } = await supabase
        .from('support_messages')
        .select('thread_id')
        .in('thread_id', threadIds)
        .is('read_at', null)
        .neq('user_id', user.id);

      if (error) {
        console.error('Error fetching thread unread counts:', error);
        return {};
      }

      // Count unread messages per thread
      const counts: Record<string, number> = {};
      unreadMessages?.forEach(msg => {
        counts[msg.thread_id] = (counts[msg.thread_id] || 0) + 1;
      });

      return counts;
    },
    enabled: !!user?.id && !!currentBrandId,
    refetchInterval, // Adaptive based on visibility
  });

  // Set up real-time subscription for messages (visibility-aware)
  useEffect(() => {
    if (!currentBrandId || !user?.id || !isVisible) return;

    const channel = supabase
      .channel(`support-thread-unreads-${currentBrandId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'support_messages',
        },
        () => {
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentBrandId, user?.id, refetch, isVisible]);

  return unreadCounts;
};
